import React from "react";
import { useLang } from "@/lib/i18n";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Target, Globe, BarChart3 } from "lucide-react";
import CountUp from "./CountUp";

function Eyebrow({ num, label }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">{num}</span>
      <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
      <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{label}</span>
    </div>
  );
}

export default function Stats({ stats, settings }) {
  const { t } = useLang();
  const total = settings.display_total_tips;
  const won = Math.round((total * settings.display_win_rate) / 100);
  const lost = total - won;

  return (
    <section id="performance" className="py-20 lg:py-28 border-y border-[var(--border-gold)]" style={{ background: "var(--at-darkest)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12">
          <Eyebrow num="02" label={t("stats.title")} />
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {t("stats.title")}
          </h2>
          <p className="mt-4 text-base font-body text-white/75 leading-relaxed">{t("stats.subtitle")}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div data-testid="performance-chart" className="lg:col-span-2 at-card-solid rounded-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h3 className="font-heading font-bold text-lg text-white">{t("stats.chartTitle")}</h3>
                <div className="flex items-center gap-1.5 mt-1 text-sm font-body font-semibold text-[var(--trust-gold)]">
                  <TrendingUp size={15} /> +{stats.total_profit} {t("stats.profit")}
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-xs font-body text-white/70">
                <span className="w-2.5 h-2.5 rounded-full bg-[var(--trust-gold)]" /> {t("stats.winRate")} {settings.display_win_rate}%
              </span>
            </div>
            <div className="h-64 sm:h-72 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFD700" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#FFD700" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "rgba(255,255,255,0.6)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "rgba(255,255,255,0.6)" }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid var(--border-gold)", background: "#38040C", color: "#fff", fontFamily: "Manrope", fontSize: 13 }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(v) => [`${v} ${t("stats.profit")}`, ""]}
                  />
                  <Area type="monotone" dataKey="value" stroke="#FFD700" strokeWidth={2.5} fill="url(#goldGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-[var(--border-light)]">
              <div className="text-center">
                <CountUp value={won} testid="perf-won" className="font-serif-display font-bold text-2xl text-white" />
                <div className="text-[11px] font-heading uppercase tracking-wide text-white/55 mt-1">{t("stats.won")}</div>
              </div>
              <div className="text-center border-x border-[var(--border-light)]">
                <CountUp value={settings.display_win_rate} suffix="%" className="font-serif-display font-bold text-2xl gold-gradient-text" />
                <div className="text-[11px] font-heading uppercase tracking-wide text-white/55 mt-1">{t("stats.winRate")}</div>
              </div>
              <div className="text-center">
                <CountUp value={lost} className="font-serif-display font-bold text-2xl text-white/70" />
                <div className="text-[11px] font-heading uppercase tracking-wide text-white/55 mt-1">{t("stats.lost")}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-1 gap-6">
            <div data-testid="stat-winrate" className="at-card rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-white/8 text-[var(--trust-gold)] ring-1 ring-[var(--border-gold)]"><Target size={20} /></div>
              <CountUp value={settings.display_win_rate} suffix="%" className="font-serif-display font-bold text-4xl text-white" />
              <div className="text-xs font-heading font-semibold uppercase tracking-wider text-white/60 mt-1.5">{t("stats.winRate")}</div>
            </div>
            <div data-testid="stat-totaltips-top" className="at-card rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-white/8 text-[var(--trust-gold)] ring-1 ring-[var(--border-gold)]"><BarChart3 size={20} /></div>
              <CountUp value={settings.display_total_tips} className="font-serif-display font-bold text-4xl text-white" />
              <div className="text-xs font-heading font-semibold uppercase tracking-wider text-white/60 mt-1.5">{t("stats.totalTips")}</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {[
            { icon: TrendingUp, label: t("stats.overUnder") },
            { icon: Target, label: t("stats.asianHandicap") },
            { icon: Globe, label: t("stats.coverage") },
          ].map((e, i) => (
            <div key={i} data-testid={`expertise-${i}`} className="flex items-center gap-4 at-card rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-white/8 text-[var(--trust-gold)] ring-1 ring-[var(--border-gold)]"><e.icon size={22} /></div>
              <div className="font-heading font-bold text-base text-white leading-snug">{e.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
